/**
 * Разметка исходника: `sourceToEntities` / `entitiesToSource`. docs/ui-design.md §6.5.
 *
 * ПЕРЕОПРЕДЕЛЕНИЕ НОРМЫ. `00-integration.md` §12 откладывал редактор исходника в фазу 2 и
 * называл функции `parseSourceMarkup` / `serializeToSourceMarkup`. Решение Ильи 24.08.2026:
 * редактор строится в фазе 1 целиком, имена берутся из `docs/ui-design.md` §6.5. Где §12
 * расходится с этим файлом — прав этот файл. Остальная часть §12 силы не теряет: функции
 * живут в `@smm/contracts` (вход `.`), зависимостей, кроме zod, у пакета по-прежнему нет,
 * а фикстуры остаются сырыми объектами и через разбор разметки не строятся.
 *
 * Единицы. Смещения и длины сущностей — UTF-16 code units (docs/telegram.md §2). Выходной
 * текст собирается инкрементально, и границы сущностей записываются как `out.length` — ровно
 * поэтому пересчётов единиц здесь нет вовсе, а offset не может попасть в середину суррогатной
 * пары: границы возникают только на символах разметки, а они ASCII. Это структурная гарантия
 * варианта Г (`docs/ui-design.md` §6.5), и она проверяется утверждением на всём корпусе.
 * Лимиты длины (4096/1024 КОДПОИНТОВ) здесь не считаются: парсер не знает `method`, лимит
 * зависит от него, и меряет его `checkLimits()` уже по канонической форме.
 *
 * Свойство корректности (property-тест в `packages/fixtures/src/source-markup.test.ts`):
 *
 *     sourceToEntities(entitiesToSource(x)).{text,entities} ≡ canonicalize(x)
 *
 * Именно `canonicalize(x)`: канонизация уже слила соседние однотипные, разрезала
 * пересекающиеся и упорядочила сущности, и только после неё набор выразим вложенной
 * разметкой. Ровно поэтому разбор отдаёт сущности, прогнанные через `resplitEntities` —
 * то есть уже в каноническом порядке: без этого свойство ломалось бы на сортировке.
 *
 * Пределы выразительности диалекта (следствия синтаксиса §6.5, а не дефекты реализации;
 * каждый закреплён отдельным тестом в `source-markup.test.ts`):
 *   1. Цитата занимает целые строки: `> ` — префикс строки. Цитата, начинающаяся или
 *      кончающаяся посреди строки, при сериализации притягивается к границам строк.
 *   2. Две соседние однотипные цитаты на соседних строках при разборе становятся одной:
 *      в исходнике они выглядят одинаково, и Telegram рисует их одинаково.
 *   3. Многострочная вставка `code` длиной пробега меньше `MIN_FENCE`, содержимое которой
 *      начинается или кончается обратной кавычкой либо пробелом: правило пробелов CommonMark
 *      действует в пределах одной строки (см. `unwrapCodeSpan`). Многострочный код в Telegram
 *      — это `pre`, и у него предела нет. Пробег от `MIN_FENCE` под предел не подпадает: такая
 *      вставка неотличима от забора блока кода, и сериализатор обязан обернуть её содержимое
 *      пробелами (`emitCodeLike`), а разбор — снять обёртку (`unwrap`).
 *   4. `)` внутри адреса ссылки percent-кодируется при сериализации: разбор читает адрес
 *      до первой закрывающей скобки. Ссылка остаётся той же, строка адреса — нет.
 *   5. Блок кода занимает целые строки (19.09.2026, находка publisher-wire-4): забор ```
 *      стоит на СВОЕЙ строке — открывающий с начала строки, за закрывающим на ней ничего нет.
 *      В rich-сообщении блок кода — отдельный блок (`telegram/rich.ts`), и `pre` посреди строки
 *      отбила бы заморозка (`rich_block_misaligned`) — у писателя мимо починки разметки. Разбор
 *      называет такой забор отказом `pre_language_invalid` с местом; забор, открытый в цитате,
 *      чьи строки идут без её префикса, — `entity_crosses_blockquote`. Канон с `pre` посреди
 *      строки (он приходит только мимо разметки — фикстуры, intake API) сериализуется забором
 *      посреди строки и при обороте получает тот же отказ: вставить перевод строки значило бы
 *      молча изменить текст поста.
 * Всё остальное — вложенные и соседние сущности, эмодзи вне BMP, цитаты — round-trip проходит
 * побайтово. Следствие предела 5 и префикса `> `: всё, что разбор ПРИНЯЛ, даёт цитаты и блоки
 * кода на целых строках (свойство в `source-markup-roundtrip.test.ts`).
 *
 * Экранирование при сериализации сплошное: `\ * _ ~ | ` [ ]` экранируются всегда, `>` —
 * в начале строки. Условное экранирование «только там, где иначе откроется маркер» требует
 * заглядывания через границу соседнего маркера и в markdown-реализациях служит главным
 * источником ошибок; предсказуемость здесь дороже красоты исходника.
 */

import { asUtf16, isCodeUnitBoundary, isWellFormedText } from '../units.ts';
import { isTelegramUrl, type MessageEntity } from './draft.ts';
import { type SendableEntityType, sortEntities } from './entity.ts';
import { resplitEntities } from './resplit.ts';

// ─────────────────────────────────────────────────────────────────────────────
// Диагностика

/**
 * Коды разбора. Совпадающие по смыслу с `IssueCode` названы дословно так же
 * (`entity_crosses_blockquote`, `continuous_entity_overlap`, …): у одного события не бывает
 * двух имён, и UI показывает одну и ту же формулировку, откуда бы отказ ни пришёл.
 */
export const SOURCE_ISSUE_CODES = [
  'unclosed_marker',
  'unclosed_link',
  'unclosed_fence',
  'nested_blockquote',
  'text_link_url_invalid',
  'pre_language_invalid',
  'continuous_entity_overlap',
  'entity_crosses_blockquote',
  'entity_inside_code_or_pre',
  'text_not_well_formed',
  'entity_boundary_in_surrogate_pair',
] as const;

export type SourceIssueCode = (typeof SOURCE_ISSUE_CODES)[number];

/**
 * Позиция обязательна: UI показывает ошибку разбора под полем с указанием места
 * (`docs/ui-design.md` §6.5, «Что показывается под полем»). `offset` и `column` — в UTF-16
 * code units, то есть в тех же единицах, что `selectionStart` у textarea: каретку можно
 * ставить прямо по ним, без пересчёта.
 */
export interface SourceIssue {
  readonly code: SourceIssueCode;
  /** По-русски: показывается человеку под полем. */
  readonly message: string;
  readonly offset: number;
  /** 1-based. */
  readonly line: number;
  /** 1-based, UTF-16 code units. */
  readonly column: number;
}

export type SourceParseResult =
  | { readonly ok: true; readonly text: string; readonly entities: MessageEntity[] }
  | { readonly ok: false; readonly issues: SourceIssue[] };

// ─────────────────────────────────────────────────────────────────────────────
// Синтаксис

type QuoteType = 'blockquote' | 'expandable_blockquote';
type SplittableMarkerType = 'bold' | 'italic' | 'underline' | 'strikethrough' | 'spoiler';

/** Порядок значим: двойной маркер обязан проверяться раньше одинарного (`__` перед `_`). */
const MARKERS = [
  { marker: '**', type: 'bold' },
  { marker: '__', type: 'underline' },
  { marker: '~~', type: 'strikethrough' },
  { marker: '||', type: 'spoiler' },
  { marker: '_', type: 'italic' },
] as const satisfies readonly { marker: string; type: SplittableMarkerType }[];

/** Partial, а не Record: индекс по любому типу сущности обязан давать `undefined`, а не ложь. */
const MARKER_BY_TYPE: Readonly<Partial<Record<SendableEntityType, string>>> = {
  bold: '**',
  italic: '_',
  underline: '__',
  strikethrough: '~~',
  spoiler: '||',
};

const QUOTE_MARK: Readonly<Record<QuoteType, string>> = {
  blockquote: '>',
  expandable_blockquote: '>>',
};

/** `\X` даёт литерал X. Скобки в списке ради симметрии: сами по себе они не маркеры. */
const ESCAPABLE = '\\*_~|`[]()>';
/** Что экранируется при сериализации всегда. `>` — отдельно и только в начале строки. */
const ESCAPE_ALWAYS = '\\*_~|`[]';

const MIN_FENCE = 3;
/** Совпадает с `MessageEntitySchema`: пустой остаток строки — фенс без языка. */
const LANG_RE = /^[A-Za-z0-9+#._-]{0,32}$/;

/** Отказы строки забора (`pre_language_invalid`). Забор занимает свою строку — см. `midLine`. */
const FENCE_BAD_LANGUAGE =
  'После открывающих кавычек блока кода допустимо только имя языка: буквы, цифры и + # . _ -';
const FENCE_OPENS_MID_LINE =
  'Блок кода начинается посреди строки: открывающие ``` ставятся в начало отдельной строки, ' +
  'текст перед ними — строкой выше.';
const FENCE_OPENS_MID_LINE_BAD_LANGUAGE =
  'Блок кода начинается посреди строки, и после открывающих кавычек — не имя языка: ``` и имя ' +
  'языка (буквы, цифры и + # . _ -) ставятся на отдельную строку, код — со следующей.';
const FENCE_CLOSES_MID_LINE =
  'Блок кода кончается посреди строки: после закрывающих ``` на той же строке ничего не пишется, ' +
  'текст — со следующей строки.';
const FENCE_CROSSES_QUOTE =
  'Блок кода открыт внутри цитаты, а его строки идут без её префикса: «> » ставится и на строках ' +
  'кода, и на закрывающих ```. Иначе цитата кончается на заборе, а код — нет.';

const CONTINUOUS: ReadonlySet<string> = new Set(['code', 'pre', 'text_link']);
const QUOTES: ReadonlySet<string> = new Set(['blockquote', 'expandable_blockquote']);

// ─────────────────────────────────────────────────────────────────────────────
// Мелкие помощники

function positionOf(src: string, offset: number): { offset: number; line: number; column: number } {
  const at = offset < 0 ? 0 : offset > src.length ? src.length : offset;
  let line = 1;
  let lastBreak = -1;
  for (let i = 0; i < at; i++) {
    if (src.charCodeAt(i) === 0x0a) {
      line++;
      lastBreak = i;
    }
  }
  return { offset: at, line, column: at - lastBreak };
}

/** Длина пробега обратных кавычек, начинающегося ровно на `i`. */
function backtickRunAt(s: string, i: number): number {
  let n = 0;
  while (s.charAt(i + n) === '`') n++;
  return n;
}

/** Индекс пробега обратных кавычек длиной РОВНО `n`, начиная с `from`; -1 — нет такого. */
function findExactRun(s: string, from: number, n: number): number {
  for (let i = from; i < s.length; i++) {
    if (s.charAt(i) !== '`') continue;
    const run = backtickRunAt(s, i);
    if (run === n) return i;
    i += run - 1;
  }
  return -1;
}

/** Самый длинный пробег обратных кавычек в строке — по нему выбирается длина фенса. */
function maxBacktickRun(s: string): number {
  let max = 0;
  for (let i = 0; i < s.length; i++) {
    if (s.charAt(i) !== '`') continue;
    const run = backtickRunAt(s, i);
    if (run > max) max = run;
    i += run - 1;
  }
  return max;
}

/**
 * Правило пробелов CommonMark для однострочной вставки кода: пробел с обеих сторон
 * снимается. Без него содержимое, начинающееся или кончающееся обратной кавычкой, невыразимо
 * вовсе: `` ``` `` пришлось бы отличать от фенса длиной 3, а `` `` `a` `` `` — от пробега 5.
 */
function unwrapCodeSpan(content: string): string {
  if (content.length < 2) return content;
  if (!content.startsWith(' ') || !content.endsWith(' ')) return content;
  if (content.trim() === '') return content;
  return content.slice(1, content.length - 1);
}

/** Обратная сторона `unwrapCodeSpan`: когда содержимое обязано ехать в пробелах. */
function needsCodeSpanPadding(content: string): boolean {
  if (content.includes('\n')) return false;
  if (content.startsWith('`') || content.endsWith('`')) return true;
  return content.startsWith(' ') && content.endsWith(' ') && content.trim() !== '';
}

interface Range {
  start: number;
  end: number;
}
const rangesOverlap = (a: Range, b: Range): boolean => a.start < b.end && b.start < a.end;
const rangeContains = (outer: Range, inner: Range): boolean =>
  outer.start <= inner.start && outer.end >= inner.end;

// ─────────────────────────────────────────────────────────────────────────────
// Разбор

interface RawEntity {
  type: SendableEntityType;
  /** Смещения в ВЫХОДНОМ тексте, UTF-16. */
  start: number;
  end: number;
  /** Смещение в ИСХОДНИКЕ — только для позиции в диагностике. */
  src: number;
  url?: string;
  language?: string;
}

interface OpenMark {
  type: SendableEntityType;
  marker: string;
  start: number;
  src: number;
}

interface CodeSpanState {
  code: {
    n: number;
    start: number;
    src: number;
    quote: QuoteType | null;
    /** Пробег длиной от `MIN_FENCE`: содержимое такой вставки едет в пробелах-обёртках. */
    unwrap: boolean;
  } | null;
}

interface FenceOpen {
  n: number;
  language: string;
  src: number;
}

interface SourceLine {
  start: number;
  text: string;
}

function splitSourceLines(src: string): SourceLine[] {
  const lines: SourceLine[] = [];
  let start = 0;
  for (let i = 0; i <= src.length; i++) {
    if (i === src.length || src.charCodeAt(i) === 0x0a) {
      lines.push({ start, text: src.slice(start, i) });
      start = i + 1;
    }
  }
  return lines;
}

/** `>`/`>>` плюс не более одного пробела. Пустая строка цитаты пишется одним `>`. */
function quotePrefixAt(text: string): { type: QuoteType; body: number } | null {
  if (text.charAt(0) !== '>') return null;
  const type: QuoteType = text.charAt(1) === '>' ? 'expandable_blockquote' : 'blockquote';
  let body = type === 'expandable_blockquote' ? 2 : 1;
  if (text.charAt(body) === ' ') body++;
  return { type, body };
}

/**
 * Разбор разметки исходника. Наружу не бросает НИКОГДА: любой отказ — это `ok:false`
 * со списком проблем, у каждой из которых есть позиция.
 *
 * Сущности на выходе уже канонически упорядочены и разрезаны (`resplitEntities`), поэтому
 * пересекающиеся splittable-маркеры («жирный кончился внутри курсива») — не ошибка: их
 * разрежет resplit ровно так же, как это сделал бы сервер. Ошибка — только то, что сервер
 * молча испортил бы: неразрывная сущность через границу цитаты, две неразрывные внахлёст,
 * незакрытый маркер, вложенная цитата.
 */
export function sourceToEntities(source: string): SourceParseResult {
  const issues: SourceIssue[] = [];
  const raw: RawEntity[] = [];
  const stack: OpenMark[] = [];
  let out = '';
  let needSeparator = false;
  /** Тип цитаты текущей строки: его запоминает многострочная вставка кода при открытии. */
  let lineQuote: QuoteType | null = null;

  const add = (code: SourceIssueCode, message: string, offset: number): void => {
    issues.push({ code, message, ...positionOf(source, offset) });
  };

  // ── цитаты: пробег соседних строк одного типа = одна сущность
  let runType: QuoteType | null = null;
  let runStart = 0;
  let runEnd = 0;
  let runSrc = 0;
  const closeRun = (): void => {
    if (runType === null) return;
    if (runEnd > runStart) raw.push({ type: runType, start: runStart, end: runEnd, src: runSrc });
    runType = null;
  };

  let fence: {
    n: number;
    language: string;
    quote: QuoteType | null;
    start: number;
    src: number;
  } | null = null;
  // Состояние многострочной вставки кода живёт в объекте, а не в `let`: его правит вложенный
  // сканер, а сужение типа у `let` через границу замыкания компилятор не переносит.
  const pending: CodeSpanState = { code: null };

  /** Парный маркер: второе появление закрывает первое, где бы оно ни лежало в стеке. */
  const toggle = (type: SendableEntityType, marker: string, src: number): void => {
    for (let i = stack.length - 1; i >= 0; i--) {
      const open = stack[i];
      if (open === undefined || open.type !== type) continue;
      stack.splice(i, 1);
      // Сущность нулевой длины (`****`) отбрасывается молча: Telegram такую не принимает,
      // а человеку она ничего не значит.
      if (out.length > open.start) {
        raw.push({ type, start: open.start, end: out.length, src: open.src });
      }
      return;
    }
    stack.push({ type, marker, start: out.length, src });
  };

  const closeCodeSpan = (start: number, src: number, content: string): void => {
    if (content.length === 0) return;
    raw.push({ type: 'code', start, end: start + content.length, src });
  };

  const closeLink = (body: string, i: number, bodySrc: number): number => {
    for (let k = stack.length - 1; k >= 0; k--) {
      const open = stack[k];
      if (open === undefined || open.type !== 'text_link') continue;
      const close = body.indexOf(')', i + 2);
      if (close === -1) {
        stack.splice(k, 1);
        add('unclosed_link', 'Ссылка без закрывающей скобки: «[текст](адрес)».', open.src);
        return -1;
      }
      const url = body.slice(i + 2, close);
      stack.splice(k, 1);
      if (!isTelegramUrl(url)) {
        add('text_link_url_invalid', 'Адрес ссылки должен начинаться с https://.', bodySrc + i + 2);
      } else if (out.length > open.start) {
        raw.push({ type: 'text_link', start: open.start, end: out.length, src: open.src, url });
      }
      return close + 1;
    }
    return -1;
  };

  /**
   * Забор блока кода стоит на СВОЕЙ строке (находка publisher-wire-4, 19.09.2026). В rich-сообщении
   * блок кода — отдельный блок и занимает целые строки (`richBlockIssues`, `telegram/rich.ts`);
   * забор посреди строки давал pre, начатый посреди строки, и при политике «все посты rich»
   * версию отбивала уже заморозка (`rich_block_misaligned`) — мимо починки разметки у писателя.
   * Здесь тот же дефект называется с местом в исходнике, и писатель его чинит.
   *
   * «Текст до забора» — любой символ ВЫХОДА на этой строке, пробел тоже: с него начался бы блок.
   * Маркеры без текста (`**`) выхода не дают и строку не занимают. Код — `pre_language_invalid`:
   * это отказ того же рода, что «после кавычек не имя языка», — строка забора неправильна, — и
   * он уже объяснён писателю в `knowledge/publishing/telegram.md` («Коды отказа»).
   */
  const midLine = (): boolean => out.length > 0 && out.charCodeAt(out.length - 1) !== 0x0a;

  /** Сканер одной строки тела. Возвращает открывшийся фенс — или null. */
  const scanInline = (body: string, bodySrc: number, from: number): FenceOpen | null => {
    let i = from;
    while (i < body.length) {
      const c = body.charAt(i);

      // Многострочная вставка кода: до закрывающего пробега всё — сырьё.
      const code = pending.code;
      if (code !== null) {
        const run = backtickRunAt(body, i);
        if (run === code.n) {
          const raw = out.slice(code.start);
          const content = code.unwrap ? unwrapCodeSpan(raw) : raw;
          if (content !== raw) out = out.slice(0, code.start) + content;
          closeCodeSpan(code.start, code.src, content);
          pending.code = null;
          i += run;
          continue;
        }
        const take = run > 0 ? run : 1;
        out += body.slice(i, i + take);
        i += take;
        continue;
      }

      if (c === '\\') {
        const next = body.charAt(i + 1);
        if (next !== '' && ESCAPABLE.includes(next)) {
          out += next;
          i += 2;
          continue;
        }
        out += '\\';
        i += 1;
        continue;
      }

      if (c === '`') {
        const n = backtickRunAt(body, i);
        const rest = body.slice(i + n);
        if (n >= MIN_FENCE && LANG_RE.test(rest)) {
          if (midLine()) add('pre_language_invalid', FENCE_OPENS_MID_LINE, bodySrc + i);
          return { n, language: rest, src: bodySrc + i };
        }
        const close = findExactRun(body, i + n, n);
        if (close !== -1) {
          const content = unwrapCodeSpan(body.slice(i + n, close));
          const start = out.length;
          out += content;
          closeCodeSpan(start, bodySrc + i, content);
          i = close + n;
          continue;
        }
        // Пробел сразу за пробегом — это пробел-обёртка многострочной вставки (см. ниже),
        // а не имя языка: у забора имя языка пробелом не начинается никогда. Без этой ветки
        // многострочная вставка с ``-пробегом внутри читалась как забор — отказом разбора
        // или, того хуже, молча другим текстом (M-001).
        if (n >= MIN_FENCE && !rest.startsWith(' ')) {
          // Три и больше кавычек без закрытия на строке — это попытка открыть блок кода,
          // а не вставку. Сообщать про «незакрытую вставку» здесь было бы враньём.
          // Забор ещё и посреди строки — ОДНА находка про оба дефекта: писатель чинит разметку
          // один раз, и названный только наполовину отказ вернулся бы второй попыткой.
          add(
            'pre_language_invalid',
            midLine() ? FENCE_OPENS_MID_LINE_BAD_LANGUAGE : FENCE_BAD_LANGUAGE,
            bodySrc + i,
          );
          return { n, language: '', src: bodySrc + i };
        }
        // Закрытия на этой строке нет — вставка продолжается на следующих. Пробег от трёх
        // сериализатор обязан обернуть пробелами, иначе строку не отличить от забора;
        // здесь эти пробелы снимаются — ровно правилом CommonMark.
        pending.code = {
          n,
          start: out.length,
          src: bodySrc + i,
          quote: lineQuote,
          unwrap: n >= MIN_FENCE,
        };
        out += body.slice(i + n);
        i = body.length;
        continue;
      }

      const marker = MARKERS.find((m) => body.startsWith(m.marker, i));
      if (marker !== undefined) {
        toggle(marker.type, marker.marker, bodySrc + i);
        i += marker.marker.length;
        continue;
      }

      if (c === '[') {
        stack.push({ type: 'text_link', marker: '[', start: out.length, src: bodySrc + i });
        i += 1;
        continue;
      }
      if (c === ']' && body.charAt(i + 1) === '(') {
        const next = closeLink(body, i, bodySrc);
        if (next !== -1) {
          i = next;
          continue;
        }
      }

      out += c;
      i += 1;
    }
    return null;
  };

  // ── проход по строкам исходника
  const lines = splitSourceLines(source);
  for (const line of lines) {
    let bodyStart = 0;
    let quoteType: QuoteType | null = null;
    // Внутри блока кода и внутри многострочной вставки снимается ровно тот же префикс, с
    // которым они открылись: `>` в СОДЕРЖИМОМ кода экранировать нечем, и структурный префикс
    // обязан отличаться от литерала по контексту, а не по внешнему виду. Отсутствие префикса
    // означает, что цитата кончилась, а код — нет; это ловит проверка E4.
    const insideRaw: QuoteType | null | undefined =
      fence !== null ? fence.quote : pending.code !== null ? pending.code.quote : undefined;

    if (insideRaw === undefined) {
      const prefix = quotePrefixAt(line.text);
      if (prefix !== null) {
        quoteType = prefix.type;
        bodyStart = prefix.body;
        if (line.text.charAt(bodyStart) === '>') {
          add(
            'nested_blockquote',
            'Цитата внутри цитаты: Telegram такого не поддерживает. Литеральную «>» экранируйте как \\>.',
            line.start + bodyStart,
          );
        }
      }
    } else if (insideRaw !== null) {
      const prefix = quotePrefixAt(line.text);
      if (prefix !== null && prefix.type === insideRaw) {
        quoteType = prefix.type;
        bodyStart = prefix.body;
      }
    }

    // Блок кода открыт в цитате, а ПЕРВАЯ строка после забора — без её префикса: цитата кончилась
    // на самом заборе, код — нет. Проверка пересечения (E4) этого не видит — отрезки цитаты и
    // кода касаются, а не пересекаются, — и цитата выходила с хвостовым переводом строки, то
    // есть не целыми строками (находка publisher-wire-4). Пустой блок (`out` не сдвинулся с
    // начала забора) и значит «ни одной строки кода с префиксом»: дальше E4 справится сама.
    if (
      fence !== null &&
      fence.quote !== null &&
      quoteType !== fence.quote &&
      out.length === fence.start
    ) {
      add('entity_crosses_blockquote', FENCE_CROSSES_QUOTE, line.start);
    }

    lineQuote = quoteType;
    const body = line.text.slice(bodyStart);
    const bodySrc = line.start + bodyStart;
    const closesFence = fence !== null && backtickRunAt(body, 0) === fence.n;

    if (needSeparator && !closesFence) out += '\n';

    if (quoteType !== runType) {
      closeRun();
      if (quoteType !== null) {
        runType = quoteType;
        runStart = out.length;
        runEnd = out.length;
        runSrc = line.start;
      }
    }

    let opened: FenceOpen | null = null;
    if (fence !== null && !closesFence) {
      out += body;
    } else if (fence !== null) {
      const language = fence.language;
      if (out.length > fence.start) {
        const pre: RawEntity = { type: 'pre', start: fence.start, end: out.length, src: fence.src };
        if (language !== '') pre.language = language;
        raw.push(pre);
      }
      fence = null;
      // Закрывающий забор — тоже на своей строке: текст за ним на той же строке (пробел тоже)
      // продолжил бы строку, на которой кончился блок (см. `midLine`).
      const closing = backtickRunAt(body, 0);
      const preEnd = out.length;
      opened = scanInline(body, bodySrc, closing);
      if (out.length > preEnd)
        add('pre_language_invalid', FENCE_CLOSES_MID_LINE, bodySrc + closing);
    } else {
      opened = scanInline(body, bodySrc, 0);
    }

    if (opened !== null) {
      fence = {
        n: opened.n,
        language: opened.language,
        quote: quoteType,
        start: out.length,
        src: opened.src,
      };
      needSeparator = false;
    } else {
      needSeparator = true;
    }

    if (runType !== null) runEnd = out.length;
  }

  closeRun();
  for (const open of stack) {
    if (open.type === 'text_link') {
      add('unclosed_link', 'Ссылка без закрывающей скобки: «[текст](адрес)».', open.src);
    } else {
      add('unclosed_marker', `Маркер ${open.marker} не закрыт.`, open.src);
    }
  }
  if (pending.code !== null) {
    add('unclosed_marker', 'Вставка кода не закрыта обратной кавычкой.', pending.code.src);
  }
  if (fence !== null) {
    add('unclosed_fence', 'Блок кода не закрыт: нужна строка из обратных кавычек.', fence.src);
  }
  if (!isWellFormedText(out)) {
    add('text_not_well_formed', 'В тексте есть одиночный суррогат UTF-16.', 0);
  }
  if (issues.length > 0) return { ok: false, issues };

  return finishParse(source, out, raw, issues, add);
}

/**
 * Проверки, которые повторяют шаги E2–E4 `canonicalize()`, но с позицией в ИСХОДНИКЕ.
 * Дублируются не ради второй истины: авторитет по каноничности остаётся у `canonicalize()`,
 * а здесь нужно показать человеку место в тексте, которого у канонизатора нет.
 */
function finishParse(
  source: string,
  text: string,
  raw: readonly RawEntity[],
  issues: SourceIssue[],
  add: (code: SourceIssueCode, message: string, offset: number) => void,
): SourceParseResult {
  const quotes = raw.filter((e) => QUOTES.has(e.type));
  const continuous = raw.filter((e) => CONTINUOUS.has(e.type));

  for (let i = 0; i < continuous.length; i++) {
    for (let j = i + 1; j < continuous.length; j++) {
      const a = continuous[i];
      const b = continuous[j];
      if (a === undefined || b === undefined) continue;
      if (rangesOverlap(a, b)) {
        add(
          'continuous_entity_overlap',
          `Неразрывные сущности накладываются: ${a.type} и ${b.type}. Код и ссылка не вкладываются друг в друга.`,
          Math.max(a.src, b.src),
        );
      }
    }
  }

  for (const c of continuous) {
    for (const q of quotes) {
      if (rangesOverlap(c, q) && !rangeContains(q, c)) {
        add(
          'entity_crosses_blockquote',
          `Сущность ${c.type} пересекает границу цитаты — Telegram выбросит её молча.`,
          c.src,
        );
      }
    }
  }

  if (issues.length > 0) return { ok: false, issues };

  const { entities, dropped_in_pre } = resplitEntities(raw.map(toMessageEntity));
  for (const d of dropped_in_pre) {
    const owner = raw.find(
      (e) => e.type === d.type && e.start <= d.offset && e.end >= d.offset + d.length,
    );
    add(
      'entity_inside_code_or_pre',
      `Форматирование ${d.type} внутри блока кода пропадёт: Telegram его не сохраняет.`,
      owner?.src ?? 0,
    );
  }

  // Тревожная нить, а не рабочая ветка: границы сущностей возникают только на ASCII-символах
  // разметки, поэтому попасть в середину суррогатной пары они не могут в принципе. Если
  // проверка когда-нибудь сработает, сломан сканер, и увидеть это надо здесь.
  for (const e of entities) {
    if (!isCodeUnitBoundary(text, e.offset) || !isCodeUnitBoundary(text, e.offset + e.length)) {
      add(
        'entity_boundary_in_surrogate_pair',
        'Граница сущности попала в середину суррогатной пары.',
        source.length,
      );
    }
  }

  if (issues.length > 0) return { ok: false, issues };
  return { ok: true, text, entities };
}

/** Та же сущность с другим диапазоном. Спред по union'у здесь не проходит по типам. */
function withRange(e: MessageEntity, start: number, end: number): MessageEntity {
  const offset = asUtf16(start);
  const length = asUtf16(end - start);
  switch (e.type) {
    case 'text_link':
      return { type: 'text_link', offset, length, url: e.url };
    case 'pre':
      return e.language === undefined
        ? { type: 'pre', offset, length }
        : { type: 'pre', offset, length, language: e.language };
    default:
      return { type: e.type, offset, length };
  }
}

function toMessageEntity(e: RawEntity): MessageEntity {
  const offset = asUtf16(e.start);
  const length = asUtf16(e.end - e.start);
  switch (e.type) {
    case 'text_link':
      return { type: 'text_link', offset, length, url: e.url ?? '' };
    case 'pre':
      return e.language === undefined
        ? { type: 'pre', offset, length }
        : { type: 'pre', offset, length, language: e.language };
    default:
      return { type: e.type, offset, length };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Сериализация

interface OutLine {
  /** Смещение в ТЕКСТЕ на момент начала строки исходника: по нему берётся префикс цитаты. */
  textOffset: number;
  buf: string;
}

interface TextLine {
  start: number;
  end: number;
}

function textLines(text: string): TextLine[] {
  const lines: TextLine[] = [];
  let start = 0;
  for (let i = 0; i <= text.length; i++) {
    if (i === text.length || text.charCodeAt(i) === 0x0a) {
      lines.push({ start, end: i });
      start = i + 1;
    }
  }
  return lines;
}

/**
 * Типы, у которых ДВЕ соседние сущности в исходнике неразличимы от одной, и потому перед
 * выдачей соседей надо слить:
 *   `italic` — единственный одинарный маркер: «закрыли и тут же открыли» дало бы `__`,
 *              то есть подчёркнутый;
 *   `code`, `pre` — соседние пробеги обратных кавычек склеиваются в один длинный, и разбор
 *              съедает кавычки внутрь ТЕКСТА. Это порча текста, а не потеря форматирования.
 *
 * Набор сущностей от слияния не меняется: `resplitEntities` при разборе восстанавливает
 * разрезы ровно там, где их сделала канонизация (пробеги режутся по началам чужих пробегов).
 *
 * Слияние отменяется в одном случае: слитый диапазон начал бы частично пересекаться с
 * НЕРАЗРЫВНОЙ сущностью (code, pre, text_link). Такое пересечение обход выдачи чинит
 * «закрыть и открыть заново», а заново открытая ссылка — это вторая ссылка и второй якорь.
 * С частичным пересечением splittable-сущностей друг с другом обход справляется сам.
 */
const MERGE_ADJACENT: ReadonlySet<string> = new Set(['italic', 'code', 'pre']);

function sameDecoration(a: MessageEntity, b: MessageEntity): boolean {
  if (a.type !== b.type) return false;
  if (a.type === 'pre' && b.type === 'pre') return a.language === b.language;
  if (a.type === 'text_link' && b.type === 'text_link') return a.url === b.url;
  return true;
}

function partiallyOverlaps(f: MessageEntity, start: number, end: number): boolean {
  const fs = f.offset;
  const fe = f.offset + f.length;
  if (fs >= end || fe <= start) return false;
  return !(start <= fs && fe <= end) && !(fs <= start && end <= fe);
}

function mergeAdjacent(sorted: readonly MessageEntity[]): MessageEntity[] {
  const out = sorted.slice();
  for (let i = 0; i < out.length; i++) {
    const a = out[i];
    if (a === undefined || !MERGE_ADJACENT.has(a.type)) continue;
    for (let j = 0; j < out.length; j++) {
      const b = out[j];
      if (j === i || b === undefined || !sameDecoration(a, b)) continue;
      if (a.offset + a.length !== b.offset) continue;
      const start = a.offset;
      const end = b.offset + b.length;
      const blocked = out.some(
        (f, k) =>
          k !== i &&
          k !== j &&
          f !== undefined &&
          CONTINUOUS.has(f.type) &&
          partiallyOverlaps(f, start, end),
      );
      if (blocked) continue;
      const merged = withRange(a, start, end);
      out.splice(Math.max(i, j), 1);
      out.splice(Math.min(i, j), 1);
      out.push(merged);
      i = -1; // набор изменился — начинаем поиск заново
      break;
    }
  }
  return sortEntities(out);
}

/** `)` в адресе ломает разбор `[текст](адрес)`; управляющие символы ломают строчную модель. */
function encodeUrlForSource(url: string): string {
  let out = '';
  for (let i = 0; i < url.length; i++) {
    const code = url.charCodeAt(i);
    out +=
      url.charAt(i) === ')' || code < 0x20
        ? `%${code.toString(16).toUpperCase().padStart(2, '0')}`
        : url.charAt(i);
  }
  return out;
}

/**
 * Каноническая форма → разметка исходника. Не бросает: вход, который диалект выразить не
 * может (см. пределы выразительности в шапке), сериализуется как можно ближе к смыслу.
 */
export function entitiesToSource(draft: {
  text: string;
  entities: readonly MessageEntity[];
}): string {
  const text = draft.text;
  const sorted = mergeAdjacent(sortEntities(draft.entities));
  const inline = sorted.filter((e) => !QUOTES.has(e.type));
  const lines = textLines(text);

  // Строка принадлежит цитате, если лежит в ней целиком: так притягивание к границам строк
  // детерминировано, и две непересекающиеся цитаты не могут претендовать на одну строку.
  // Запасная ветка ниже — для цитаты, которая не покрывает целиком НИ ОДНОЙ непустой строки.
  const quoteOfLine: (QuoteType | null)[] = lines.map(() => null);
  for (const q of sorted) {
    if (!QUOTES.has(q.type)) continue;
    const qEnd = q.offset + q.length;
    const type: QuoteType =
      q.type === 'expandable_blockquote' ? 'expandable_blockquote' : 'blockquote';
    // `taken` считает только НЕПУСТЫЕ строки: цитата, целиком составленная из пустых,
    // при разборе даёт пробег нулевой длины и точно так же исчезает.
    let taken = false;
    lines.forEach((l, i) => {
      if (quoteOfLine[i] === null && q.offset <= l.start && l.end <= qEnd) {
        quoteOfLine[i] = type;
        if (l.end > l.start) taken = true;
      }
    });
    // Цитата, не покрывающая ни одной строки ЦЕЛИКОМ, иначе не выводилась бы вовсе — ни
    // префикса, ни диагностики, цитата просто исчезала из поста (M-002). Предел 1 разрешает
    // притянуть её к границам строк, но не потерять: берём строки, которые она задевает.
    if (!taken) {
      lines.forEach((l, i) => {
        if (quoteOfLine[i] === null && q.offset <= l.end && l.start <= qEnd) quoteOfLine[i] = type;
      });
    }
  }

  const out: OutLine[] = [{ textOffset: 0, buf: '' }];
  const emit = (s: string): void => {
    const line = out[out.length - 1];
    if (line !== undefined) line.buf += s;
  };
  const breakLine = (textOffset: number): void => {
    out.push({ textOffset, buf: '' });
  };
  const atLineStart = (): boolean => (out[out.length - 1]?.buf ?? '') === '';

  /** Сырьё блока кода: экранирования внутри нет, переводы строк идут через `breakLine`. */
  const emitRawLines = (content: string, base: number): void => {
    let from = 0;
    for (let i = 0; i <= content.length; i++) {
      if (i === content.length) {
        emit(content.slice(from, i));
        break;
      }
      if (content.charCodeAt(i) === 0x0a) {
        emit(content.slice(from, i));
        breakLine(base + i + 1);
        from = i + 1;
      }
    }
  };

  const emitCodeLike = (e: MessageEntity, content: string): void => {
    if (e.type === 'pre') {
      const n = Math.max(MIN_FENCE, maxBacktickRun(content) + 1);
      emit('`'.repeat(n) + (e.language ?? ''));
      breakLine(e.offset);
      emitRawLines(content, e.offset);
      breakLine(e.offset + e.length);
      emit('`'.repeat(n));
      return;
    }
    const n = maxBacktickRun(content) + 1;
    // Многострочная вставка, открывающаяся пробегом от трёх, выглядит в точности как забор
    // блока кода: `\`\`\`js\n…` читается и так и так, и разбор выбирал забор — то есть
    // ДРУГОЙ текст. Неоднозначность снимает пробел-обёртка: имя языка у забора пробелом не
    // начинается никогда, а разбор снимает обёртку правилом CommonMark (M-001).
    const fenceLike = content.includes('\n') && n >= MIN_FENCE;
    const pad = needsCodeSpanPadding(content) || fenceLike ? ' ' : '';
    emit('`'.repeat(n) + pad);
    emitRawLines(content, e.offset);
    emit(pad + '`'.repeat(n));
  };

  const openMarker = (e: MessageEntity): void => {
    if (e.type === 'text_link') {
      emit('[');
      return;
    }
    const marker = MARKER_BY_TYPE[e.type];
    if (marker !== undefined) emit(marker);
  };
  const closeMarker = (e: MessageEntity): void => {
    if (e.type === 'text_link') {
      emit(`](${encodeUrlForSource(e.url)})`);
      return;
    }
    const marker = MARKER_BY_TYPE[e.type];
    if (marker !== undefined) emit(marker);
  };

  const open: MessageEntity[] = [];
  let i = 0;
  let k = 0;
  for (;;) {
    // Закрытие. Кончиться может не только верхняя сущность: после слияния соседних
    // splittable-пробегов они законно пересекаются друг с другом. Тогда всё, что лежит
    // выше, закрывается и открывается заново — resplit склеит куски обратно при разборе.
    const done = open.findIndex((e) => e.offset + e.length <= i);
    if (done !== -1) {
      const above = open.slice(done + 1);
      for (let d = open.length - 1; d >= done; d--) {
        const e = open[d];
        if (e !== undefined) closeMarker(e);
      }
      open.length = done;
      for (const e of above) {
        if (e.offset + e.length <= i) continue;
        openMarker(e);
        open.push(e);
      }
    }
    // Сущности, оставшиеся позади (внутри уже выданного блока кода), выдаче не подлежат:
    // сервер выбросил бы их сам — ровно то, что делает dropped_in_pre в resplit.
    while (k < inline.length && (inline[k]?.offset ?? i) < i) k++;

    let jumped = false;
    while (k < inline.length) {
      const e = inline[k];
      if (e === undefined || e.offset !== i) break;
      k++;
      if (e.type === 'code' || e.type === 'pre') {
        emitCodeLike(e, text.slice(e.offset, e.offset + e.length));
        i = e.offset + e.length;
        jumped = true;
        break;
      }
      openMarker(e);
      open.push(e);
    }
    if (jumped) continue;
    if (i >= text.length) break;

    const c = text.charAt(i);
    if (c === '\n') breakLine(i + 1);
    else if (c === '>') emit(atLineStart() ? '\\>' : '>');
    else emit(ESCAPE_ALWAYS.includes(c) ? `\\${c}` : c);
    i += 1;
  }
  while (open.length > 0) {
    const top = open.pop();
    if (top !== undefined) closeMarker(top);
  }

  // Префикс цитаты навешивается последним: строк исходника больше, чем строк текста
  // (у блока кода есть свои строки-ограничители), и каждая из них обязана нести префикс.
  return out
    .map((line) => {
      const type = quoteOfLine[lineIndexAt(lines, line.textOffset)] ?? null;
      if (type === null) return line.buf;
      return QUOTE_MARK[type] + (line.buf === '' ? '' : ` ${line.buf}`);
    })
    .join('\n');
}

function lineIndexAt(lines: readonly TextLine[], offset: number): number {
  for (let i = lines.length - 1; i >= 0; i--) {
    const l = lines[i];
    if (l !== undefined && offset >= l.start) return i;
  }
  return 0;
}
